import HttpService from './HttpService';

const API_URL = '/user-floor-plans';

/**
 * Format error for consistent handling across components
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Default error message
 * @returns {Object} - Structured error with message and code
 */
const formatError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.error || error.response?.data?.message || defaultMessage;
  const errorCode = error.response?.status;
  const details = error.response?.data?.errors || error.response?.data?.details || {};

  if (process.env.NODE_ENV !== 'production') {
    console.error(defaultMessage, error);
  }

  return { message: errorMessage, code: errorCode, originalError: error, details };
};

/**
 * Service for user-created floor plans. Distinct from room scans (immutable
 * captures read via RoomService.getRoomScan) — plans are editable documents
 * but serve the same viewer-ready data shape.
 */
const UserFloorPlanService = {
  /**
   * List the user's floor plans for a room (lightweight summaries).
   * @param {number} roomId
   * @returns {Promise<{plans: Array, total: number}>}
   */
  getPlansForRoom: async (roomId) => {
    try {
      const response = await HttpService.get(`${API_URL}/room/${roomId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching floor plans for room ${roomId}`);
    }
  },

  /**
   * Get one plan with full viewer-ready geometry.
   * @param {number} planId
   * @returns {Promise<Object>} - Plan with `data` blob shaped like a scan response
   */
  getPlan: async (planId) => {
    try {
      const response = await HttpService.get(`${API_URL}/${planId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching floor plan ${planId}`);
    }
  },

  /**
   * Create an empty plan with one starter section.
   * @param {Object} data - {room_id, name?, label?, wall_height_m?}
   * @returns {Promise<Object>} - Full plan response
   */
  createPlan: async (data) => {
    try {
      const response = await HttpService.post(API_URL, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error creating floor plan');
    }
  },

  /**
   * Update plan metadata (name).
   * @param {number} planId
   * @param {Object} data - {name?}
   * @returns {Promise<Object>} - Plan summary
   */
  updatePlan: async (planId, data) => {
    try {
      const response = await HttpService.patch(`${API_URL}/${planId}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error updating floor plan ${planId}`);
    }
  },

  /**
   * Replace the plan's full geometry. Body comes from
   * `buildSavePayload` in utils/floorPlanGeometry.js.
   * @param {number} planId
   * @param {Object} payload - {sections: [...]}
   * @returns {Promise<Object>} - Full plan response with recomputed floor areas
   */
  saveSurfaces: async (planId, payload) => {
    try {
      const response = await HttpService.put(`${API_URL}/${planId}/surfaces`, payload);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error saving floor plan ${planId}`);
    }
  },

  /**
   * Delete a plan and all its geometry.
   * @param {number} planId
   * @returns {Promise<Object>}
   */
  deletePlan: async (planId) => {
    try {
      const response = await HttpService.delete(`${API_URL}/${planId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error deleting floor plan ${planId}`);
    }
  },

  /**
   * Snapshot-convert the room's latest captured scan into an editable plan.
   * @param {Object} data - {room_id, name?, scan_id?}
   * @returns {Promise<Object>} - Full plan response (source: 'scan_copy')
   */
  createFromScan: async (data) => {
    try {
      const response = await HttpService.post(`${API_URL}/from-scan`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error converting scan to floor plan');
    }
  },
};

export default UserFloorPlanService;
